import { isRecord, levelsEqual, pollVotesEqual } from "./http";
import { slotStarts } from "./slots";
import type { EventDef, EventState, Level, Locked, Participant, ParticipantSave, PollDef, PollVote, SavePatch, WsMessage } from "./types";

export interface Draft {
  name: string;
  note: string;
  availability: Record<string, Level>;
  pollVotes: Record<string, Record<string, PollVote>>;
}

export type SaveBody = ParticipantSave & Required<Pick<ParticipantSave, "saveSeq" | "baseSeq" | "origin">>;

export type SaveStatus = "idle" | "dirty" | "saving" | "saved" | "retrying" | "error" | "locked" | "gone";

/** One request that has left for the server and not yet settled. `sent` is a snapshot of the
 * draft taken when the ticket was created, so a later local edit cannot change what this
 * particular request actually carries. `saveSeq` increases within one identity; beginJoin resets
 * that counter, so only the ticket object itself identifies its response. */
export interface Ticket {
  editSeq: number;
  saveSeq: number;
  baseSeq: number;
  keepalive: boolean;
  sent: Draft;
  /** Changes against the stored row at mint time, only for a keepalive with an existing row. */
  patch?: SavePatch;
  prunedAtSend: number;
}

export interface Sync {
  server: EventState | null;
  draft: Draft | null;
  pid: string | null;
  editSeq: number;
  savedSeq: number;
  status: SaveStatus;
  message: string;
  prunedCells: number;
  prunedSticky: number;
  seenOnServer: boolean;
  /** This tab's tag, given to `initial()` and stamped on every ticket's body. */
  origin: string;
  /** Every request that has left and not settled. */
  inFlight: Ticket[];
  /** Highest saveSeq seen on any copy of my own row (state, delta, or save response). */
  seqBase: number;
  /** Highest saveSeq this tab has ever put on a ticket. */
  seqIssued: number;
}

export interface SaveResponse {
  state: EventState;
  participant: Participant;
  joined: boolean;
  ignored: number;
  stale: boolean;
}

export function initial(pid: string | null, origin: string): Sync {
  return {
    server: null,
    draft: null,
    pid,
    editSeq: 0,
    savedSeq: 0,
    status: "idle",
    message: "",
    prunedCells: 0,
    prunedSticky: 0,
    seenOnServer: false,
    origin,
    inFlight: [],
    seqBase: 0,
    seqIssued: 0,
  };
}

export function isDirty(s: Sync): boolean {
  return s.editSeq > s.savedSeq;
}

/** Ordinary path: a draft, dirty, status not error/locked/gone, and NO ticket in flight. */
export function shouldSaveNow(s: Sync): boolean {
  if (!s.draft) return false;
  if (!isDirty(s)) return false;
  if (s.status === "locked" || s.status === "gone" || s.status === "error") return false;
  if (s.inFlight.length > 0) return false;
  return true;
}

/** Tab-close path: a draft, dirty, status not error/locked/gone, and no KEEPALIVE ticket in
 * flight whose editSeq === s.editSeq. Unlike `shouldSaveNow`, an ordinary (non-keepalive) ticket
 * already in flight does not block this: the browser cancels an ordinary request when the page
 * goes away, so the flush must go out anyway. */
export function shouldFlushNow(s: Sync): boolean {
  if (!s.draft) return false;
  if (!isDirty(s)) return false;
  if (s.status === "locked" || s.status === "gone" || s.status === "error") return false;
  if (s.inFlight.some((t) => t.keepalive && t.editSeq === s.editSeq)) return false;
  return true;
}

/** True when the current pid/edit token cannot possibly work and pressing Join should mint fresh
 * ones: gone, never joined, or no draft. Otherwise a press should reuse the same identity (only
 * setting the name through `edit`), so a retry of a join whose answer was lost looks like the same
 * person trying again rather than a new ghost holding the name hostage. */
export function joinNeedsFreshKeys(s: Sync): boolean {
  return s.status === "gone" || s.pid === null || s.draft === null;
}

/** A single stale answer is what losing a race to my own other tab looks like: worth resending at
 * once. Two in a row mean real contention, worth the existing retry backoff instead of hammering
 * the server. True only when both the previous and this answer were stale; any other combination
 * (including this one being a genuine, non-stale success) resends immediately. */
export function staleNeedsBackoff(previousWasStale: boolean, thisIsStale: boolean): boolean {
  return previousWasStale && thisIsStale;
}

export function acknowledgePrune(s: Sync): Sync {
  if (s.prunedCells <= 0) return s;
  return { ...s, prunedSticky: s.prunedCells, prunedCells: 0 };
}

export function edit(s: Sync, change: (d: Draft) => Draft): Sync {
  if (s.status === "locked" || s.status === "gone") return s;
  const base = s.draft ?? { name: "", note: "", availability: {}, pollVotes: emptyVoteMap() };
  return {
    ...s,
    draft: change(base),
    editSeq: s.editSeq + 1,
    status: "dirty",
    message: "",
    prunedCells: 0,
    prunedSticky: 0,
  };
}

/** Legal from every status, unlike `edit`: `gone`'s guard would otherwise make the join form that
 * reappears after a removal permanently inert. Rotates in a freshly generated pid (the caller
 * already discarded the old pid/edit token), so a state that predates this join landing on the
 * server can never be mistaken for "removed again" (`seenOnServer` starts false). Empties
 * `inFlight` and resets the save-sequence counters: this identity has never saved anything yet. */
export function beginJoin(s: Sync, pid: string, name: string): Sync {
  return {
    ...s,
    pid,
    draft: { name, note: "", availability: {}, pollVotes: emptyVoteMap() },
    editSeq: 1,
    savedSeq: 0,
    status: "dirty",
    message: "",
    prunedCells: 0,
    prunedSticky: 0,
    seenOnServer: false,
    inFlight: [],
    seqBase: 0,
    seqIssued: 0,
  };
}

/** Mints a ticket for whatever is currently in `draft` and marks it in flight. Returns null when
 * there is no draft to send. The caller (saver.ts) holds the returned ticket in a local variable
 * for that one request's lifetime and hands it back to `saveSucceeded`/`saveFailed` verbatim. */
export function saveStarted(s: Sync, keepalive: boolean): { sync: Sync; ticket: Ticket } | null {
  if (!s.draft) return null;
  const saveSeq = Math.max(s.seqBase, s.seqIssued) + 1;
  const base = keepalive ? s.server?.participants.find((p) => p.id === s.pid) : undefined;
  const ticket: Ticket = {
    editSeq: s.editSeq,
    saveSeq,
    baseSeq: s.seqBase,
    keepalive,
    sent: s.draft,
    ...(base ? { patch: patchFor(base, s.draft) } : {}),
    prunedAtSend: s.prunedSticky + s.prunedCells,
  };
  const sync: Sync = {
    ...s,
    seqIssued: saveSeq,
    inFlight: [...s.inFlight, ticket],
    status: "saving",
    message: "",
  };
  return { sync, ticket };
}

/** The request body for a ticket: always the snapshot the ticket carries, never a later draft. */
export function bodyFor(
  s: Sync,
  ticket: Ticket,
): SaveBody {
  return {
    name: ticket.sent.name,
    note: ticket.sent.note,
    availability: ticket.sent.availability,
    pollVotes: ticket.sent.pollVotes,
    saveSeq: ticket.saveSeq,
    baseSeq: ticket.baseSeq,
    origin: s.origin,
    ...(ticket.patch !== undefined ? { patch: ticket.patch } : {}),
  };
}

/** A close-time edit applies to whichever row is stored when it arrives. Sets stand alone;
 * clears carry the base value so they cannot erase another tab's later repaint or vote. */
export function patchFor(base: Participant, draft: Draft): SavePatch {
  const patch: SavePatch = {};
  if (draft.name !== base.name) patch.name = { to: draft.name, from: base.name };
  if (draft.note !== base.note) patch.note = { to: draft.note, from: base.note };

  const setAvailability: Record<string, Level> = {};
  const clearAvailability: Record<string, Level> = {};
  for (const key of new Set([...Object.keys(base.availability), ...Object.keys(draft.availability)])) {
    const before = base.availability[key] ?? 0;
    const after = draft.availability[key] ?? 0;
    if (before === after) continue;
    if (after !== 0) setAvailability[key] = after;
    else if (before !== 0) clearAvailability[key] = before;
  }

  const setPollVotes = emptyVoteMap();
  const clearPollVotes = emptyVoteMap();
  for (const pollId of new Set([...Object.keys(base.pollVotes), ...Object.keys(draft.pollVotes)])) {
    const before = base.pollVotes[pollId] ?? {};
    const after = draft.pollVotes[pollId] ?? {};
    for (const optionId of new Set([...Object.keys(before), ...Object.keys(after)])) {
      const oldVote = before[optionId];
      const newVote = after[optionId];
      if (oldVote === newVote) continue;
      if (newVote !== undefined) {
        const votes = setPollVotes[pollId] ?? (setPollVotes[pollId] = Object.create(null) as Record<string, PollVote>);
        votes[optionId] = newVote;
      } else if (oldVote !== undefined) {
        const votes = clearPollVotes[pollId] ?? (clearPollVotes[pollId] = Object.create(null) as Record<string, PollVote>);
        votes[optionId] = oldVote;
      }
    }
  }

  if (Object.keys(setAvailability).length > 0 || Object.keys(setPollVotes).length > 0) {
    patch.set = {
      ...(Object.keys(setAvailability).length > 0 ? { availability: setAvailability } : {}),
      ...(Object.keys(setPollVotes).length > 0 ? { pollVotes: setPollVotes } : {}),
    };
  }
  if (Object.keys(clearAvailability).length > 0 || Object.keys(clearPollVotes).length > 0) {
    patch.clear = {
      ...(Object.keys(clearAvailability).length > 0 ? { availability: clearAvailability } : {}),
      ...(Object.keys(clearPollVotes).length > 0 ? { pollVotes: clearPollVotes } : {}),
    };
  }
  return patch;
}

/** `saved` only when caught up and nothing is left in flight; `saving` while any ticket is in
 * flight; `dirty` otherwise. Callers must not apply this over `locked` or `gone`. */
function statusFromFlight(s: Sync): SaveStatus {
  if (!isDirty(s) && s.inFlight.length === 0) return "saved";
  if (s.inFlight.length > 0) return "saving";
  return "dirty";
}

export function saveSucceeded(s: Sync, response: SaveResponse, ticket: Ticket): Sync {
  if (!s.inFlight.some((t) => t === ticket)) return s; // a late callback: settled or a previous identity
  const inFlight = s.inFlight.filter((t) => t !== ticket);
  const seqBase = Math.max(s.seqBase, response.participant.saveSeq);
  let next: Sync = { ...s, inFlight, seqBase, pid: s.pid ?? response.participant.id };

  if (response.stale) {
    // The server already holds a newer row of mine. savedSeq does not move. Absorbing the
    // response's state runs the ordinary "my own row coming back" rule: an echo of my own tab's
    // newer save is left alone, a foreign tab's row is merged in. Either way the draft stays
    // dirty (unless it was truly just an echo of what is already there), so the saver resends
    // with a higher number. Guarded by the same rev check as the non-stale branch below: a stale
    // answer computed against an older state must not walk `server` backwards past a live update
    // that already moved it forward, which would also desync the next delta's rev-plus-one check.
    if (s.server === null || response.state.rev >= s.server.rev) {
      next = absorb(next, response.state);
    }
  } else {
    const extra = Math.max(0, response.ignored - ticket.prunedAtSend);
    // An older response must never replace a newer draft: caught up on edits AND not behind the
    // highest saveSeq now known for this row.
    const draftReplace = ticket.editSeq === s.editSeq && response.participant.saveSeq >= seqBase;
    next = {
      ...next,
      savedSeq: Math.max(s.savedSeq, ticket.editSeq),
      seenOnServer: true,
      draft: draftReplace ? fromParticipant(response.participant) : s.draft,
      prunedCells: extra,
    };
    if (s.server === null || response.state.rev >= s.server.rev) {
      const settledDraft = next.draft;
      next = absorb(next, response.state);
      next = { ...next, draft: settledDraft };
      if (extra > 0) next = { ...next, prunedCells: extra };
    }
  }
  if (next.status !== "locked" && next.status !== "gone") {
    next = { ...next, status: statusFromFlight(next) };
  }
  return next;
}

export function saveFailed(s: Sync, ticket: Ticket, status: number, error: string): Sync {
  if (!s.inFlight.some((t) => t === ticket)) return s;
  const inFlight = s.inFlight.filter((t) => t !== ticket);
  const base: Sync = { ...s, inFlight };
  // An old failure must not overwrite a newer success or pre-empt a newer attempt: something else
  // already settled this editSeq, either a success already recorded (savedSeq caught up) or a
  // newer ticket still on its way (still in flight).
  const preempted = s.savedSeq >= ticket.editSeq || inFlight.some((t) => t.editSeq >= ticket.editSeq);
  if (preempted) {
    if (base.status === "locked" || base.status === "gone") return base;
    return { ...base, status: statusFromFlight(base) };
  }
  if (status === 401 || status === 404) {
    return {
      ...base,
      inFlight: [],
      status: "gone",
      draft: null,
      message: status === 401 ? "This device lost its edit key for this poll" : "The organizer removed you from this poll",
    };
  }
  if (status === 409 && /lock/i.test(error)) {
    return { ...base, inFlight: [], status: "locked", message: "Locked by the organizer", draft: followRow(s.pid, s.server) };
  }
  if (status === 0 || status === 429 || status >= 500) {
    return { ...base, status: "retrying", message: error };
  }
  if (status >= 400 && status < 500) {
    return { ...base, status: "error", message: error };
  }
  return { ...base, status: "error", message: error };
}

export function applyState(s: Sync, state: EventState): Sync {
  if (s.server !== null && state.rev <= s.server.rev) return s;
  return absorb(s, state);
}

export function applyDelta(s: Sync, msg: WsMessage): { sync: Sync; refetch: boolean } {
  if (msg.type === "state") {
    if (s.server !== null && msg.state.rev <= s.server.rev) return { sync: s, refetch: false };
    if (s.server !== null && msg.state.rev !== s.server.rev + 1) return { sync: s, refetch: true };
    return { sync: absorb(s, msg.state), refetch: false };
  }
  const rev = msg.rev;
  if (s.server === null) return { sync: s, refetch: true };
  if (rev <= s.server.rev) return { sync: s, refetch: false };
  if (rev !== s.server.rev + 1) return { sync: s, refetch: true };
  if (msg.type === "removed") {
    const next: EventState = {
      ...s.server,
      rev,
      participants: s.server.participants.filter((p) => p.id !== msg.pid),
    };
    if (s.pid !== null && msg.pid === s.pid && s.seenOnServer) {
      return {
        sync: {
          ...s,
          server: next,
          draft: null,
          status: "gone",
          message: "The organizer removed you from this poll",
          inFlight: [],
          prunedCells: 0,
        },
        refetch: false,
      };
    }
    return { sync: absorb(s, next), refetch: false };
  }
  const people = s.server.participants.slice();
  const idx = people.findIndex((p) => p.id === msg.participant.id);
  if (idx >= 0) people[idx] = msg.participant;
  else people.push(msg.participant);
  const next: EventState = { ...s.server, rev, participants: people };
  return { sync: absorb(s, next), refetch: false };
}

export function view(s: Sync): EventState | null {
  if (s.server === null) return null;
  if (s.draft === null || s.pid === null) return s.server;
  const people = s.server.participants.map((p) =>
    p.id === s.pid
      ? {
          ...p,
          name: s.draft!.name,
          note: s.draft!.note,
          availability: s.draft!.availability,
          pollVotes: s.draft!.pollVotes,
        }
      : p,
  );
  return { ...s.server, participants: people };
}

function absorb(s: Sync, state: EventState): Sync {
  const inList = s.pid !== null && state.participants.some((p) => p.id === s.pid);
  const seenOnServer = s.seenOnServer || inList;
  const myRow = s.pid !== null ? state.participants.find((p) => p.id === s.pid) : undefined;
  // seqBase rises to my row's saveSeq whenever any state, delta, or save response carries it,
  // regardless of which branch below actually runs.
  const seqBase = myRow ? Math.max(s.seqBase, myRow.saveSeq) : s.seqBase;
  if (state.locked) {
    const draft = followRow(s.pid, state);
    return {
      ...s,
      server: state,
      draft,
      savedSeq: s.editSeq,
      inFlight: [],
      status: "locked",
      message: "Locked by the organizer",
      prunedCells: 0,
      seenOnServer,
      seqBase,
    };
  }
  const wasLocked = s.status === "locked" || (s.server?.locked != null);
  if (seenOnServer && s.pid !== null && !inList) {
    return {
      ...s,
      server: state,
      draft: null,
      status: "gone",
      message: "The organizer removed you from this poll",
      inFlight: [],
      prunedCells: 0,
      seenOnServer,
      seqBase,
    };
  }
  if (isDirty(s) && s.draft) {
    const eventChanged = s.server !== null && defChanged(s.server.event, state.event);
    // An echo is recognized by origin, not by comparing to what was sent: two overlapping saves
    // from this same tab can both echo back, in either order, and both must be left alone. Both
    // origins must be non-empty to count as a match: a row saved without an origin (before this
    // phase, or a body that omitted it) must never be treated as an echo of a Sync that also has
    // no origin, since neither empty string identifies any particular tab.
    const isEcho = myRow !== undefined && s.origin !== "" && myRow.origin === s.origin;
    if (eventChanged) {
      let draft = s.draft;
      let editSeq = s.editSeq;
      // A reconnect's refetch can carry both a changed event AND my own row from another origin
      // at once: merge that row in first so its cells are not silently dropped by treating this as
      // a plain prune, then prune the merged result against the new event.
      if (myRow && !isEcho) {
        const base = s.server !== null ? (s.server.participants.find((p) => p.id === s.pid) ?? null) : null;
        const merged = mergeOwnRow(base, myRow, s.draft);
        if (!draftEquals(merged, s.draft)) {
          draft = merged;
          editSeq = s.editSeq + 1;
        }
      }
      const pruned = pruneDraft(draft, state.event);
      return {
        ...s,
        server: state,
        draft: pruned.draft,
        editSeq,
        prunedCells: pruned.prunedCells,
        status: "dirty",
        seenOnServer,
        seqBase,
      };
    }
    // This state/delta carries my own row while I am still dirty: on any other tab or device this
    // is either my own save echoing back (the server broadcasts to the socket that sent it too) or
    // a real change from elsewhere. Only the latter should touch the draft.
    if (myRow) {
      if (isEcho) {
        return { ...s, server: state, seenOnServer, seqBase };
      }
      const base = s.server !== null ? (s.server.participants.find((p) => p.id === s.pid) ?? null) : null;
      const draft = mergeOwnRow(base, myRow, s.draft);
      // A merge that changes the draft is itself an edit: without bumping editSeq, a save already
      // in flight before this merge would look "caught up" when its response lands, and the cells
      // this merge just brought in would be replaced by that stale response's row.
      const changedByMerge = !draftEquals(draft, s.draft);
      return {
        ...s,
        server: state,
        draft,
        editSeq: changedByMerge ? s.editSeq + 1 : s.editSeq,
        seenOnServer,
        seqBase,
      };
    }
    return { ...s, server: state, seenOnServer, seqBase };
  }
  const draft = followRow(s.pid, state);
  let status: SaveStatus = s.status;
  if (wasLocked) status = "idle";
  else if (status === "saving" || status === "retrying") status = s.status;
  else if (!isDirty(s) && status !== "gone") status = s.status === "saved" ? "saved" : "idle";
  return {
    ...s,
    server: state,
    draft,
    status,
    seenOnServer,
    seqBase,
    message: status === "idle" || status === "saved" ? "" : s.message,
  };
}

function draftEquals(a: Draft, b: Draft): boolean {
  return (
    a.name === b.name &&
    a.note === b.note &&
    levelsEqual(a.availability, b.availability) &&
    pollVotesEqual(a.pollVotes, b.pollVotes)
  );
}

/** Three-way merge of my own row across two tabs/devices sharing one identity. `base` is my row
 * in the last server state I had (null when I have never been seen on the server, e.g. a draft
 * join that has not saved yet, in which case every base field counts as empty so any local edit
 * wins). For each field, cell and vote: if mine equals base, nothing changed here since the last
 * sync point, so theirs (the incoming value) applies; otherwise I have an unconfirmed local edit,
 * which wins. A key absent from an object counts as "no value" and follows the same rule. */
export function mergeOwnRow(base: Participant | null, theirs: Participant, mine: Draft): Draft {
  const baseName = base?.name ?? "";
  const baseNote = base?.note ?? "";
  const baseAvailability = base?.availability ?? {};
  const basePollVotes = base?.pollVotes ?? {};
  return {
    name: mine.name === baseName ? theirs.name : mine.name,
    note: mine.note === baseNote ? theirs.note : mine.note,
    availability: mergeLevels(baseAvailability, theirs.availability, mine.availability),
    pollVotes: mergePollVotes(basePollVotes, theirs.pollVotes, mine.pollVotes),
  };
}

function mergeLevels(
  base: Record<string, Level>,
  theirs: Record<string, Level>,
  mine: Record<string, Level>,
): Record<string, Level> {
  const keys = new Set([...Object.keys(base), ...Object.keys(theirs), ...Object.keys(mine)]);
  const out: Record<string, Level> = {};
  for (const k of keys) {
    const b = base[k] ?? 0;
    const m = mine[k] ?? 0;
    const v = m === b ? (theirs[k] ?? 0) : m;
    if (v !== 0) out[k] = v;
  }
  return out;
}

function mergePollVotes(
  base: Record<string, Record<string, PollVote>>,
  theirs: Record<string, Record<string, PollVote>>,
  mine: Record<string, Record<string, PollVote>>,
): Record<string, Record<string, PollVote>> {
  const pollIds = new Set([...Object.keys(base), ...Object.keys(theirs), ...Object.keys(mine)]);
  const out = emptyVoteMap();
  for (const pollId of pollIds) {
    const b = base[pollId] ?? {};
    const t = theirs[pollId] ?? {};
    const m = mine[pollId] ?? {};
    const optIds = new Set([...Object.keys(b), ...Object.keys(t), ...Object.keys(m)]);
    const inner = Object.create(null) as Record<string, PollVote>;
    for (const optId of optIds) {
      const bv = b[optId];
      const mv = m[optId];
      const v = mv === bv ? t[optId] : mv;
      if (v !== undefined) inner[optId] = v;
    }
    out[pollId] = inner;
  }
  return out;
}

function followRow(pid: string | null, state: EventState | null): Draft | null {
  if (pid === null || state === null) return null;
  const me = state.participants.find((p) => p.id === pid);
  if (!me) return null;
  return fromParticipant(me);
}

function fromParticipant(p: Participant): Draft {
  return {
    name: p.name,
    note: p.note,
    availability: { ...p.availability },
    pollVotes: cloneVotes(p.pollVotes),
  };
}

function cloneVotes(votes: Record<string, Record<string, PollVote>>): Record<string, Record<string, PollVote>> {
  const out = Object.create(null) as Record<string, Record<string, PollVote>>;
  for (const [k, v] of Object.entries(votes)) {
    const inner = Object.create(null) as Record<string, PollVote>;
    for (const [oid, vote] of Object.entries(v)) inner[oid] = vote;
    out[k] = inner;
  }
  return out;
}

export function emptyVoteMap(): Record<string, Record<string, PollVote>> {
  return Object.create(null) as Record<string, Record<string, PollVote>>;
}

export function isWsMessage(v: unknown): v is WsMessage {
  if (!isRecord(v) || typeof v.type !== "string") return false;
  if (v.type === "state") return asEventState(v.state) !== null;
  if (typeof v.rev !== "number" || !Number.isInteger(v.rev)) return false;
  if (v.type === "removed") return typeof v.pid === "string";
  if (v.type === "participant") return asParticipant(v.participant) !== null;
  return false;
}

function asLevel(v: unknown): Level | null {
  return v === 0 || v === 1 || v === 2 || v === 3 ? v : null;
}

function asPollVote(v: unknown): PollVote | null {
  return v === 0 || v === 1 || v === 2 ? v : null;
}

function asStringArray(v: unknown): string[] | null {
  if (!Array.isArray(v)) return null;
  const out: string[] = [];
  for (const item of v) {
    if (typeof item !== "string") return null;
    out.push(item);
  }
  return out;
}

function asAvailability(v: unknown): Record<string, Level> | null {
  if (!isRecord(v)) return null;
  const out: Record<string, Level> = Object.create(null) as Record<string, Level>;
  for (const [k, val] of Object.entries(v)) {
    const lv = asLevel(val);
    if (lv === null) return null;
    out[k] = lv;
  }
  return out;
}

function asPollVotesMap(v: unknown): Record<string, Record<string, PollVote>> | null {
  if (!isRecord(v)) return null;
  const out: Record<string, Record<string, PollVote>> = emptyVoteMap();
  for (const [pollId, votes] of Object.entries(v)) {
    if (!isRecord(votes)) return null;
    const inner: Record<string, PollVote> = Object.create(null) as Record<string, PollVote>;
    for (const [oid, vote] of Object.entries(votes)) {
      const pv = asPollVote(vote);
      if (pv === null) return null;
      inner[oid] = pv;
    }
    out[pollId] = inner;
  }
  return out;
}

function asParticipant(v: unknown): Participant | null {
  if (!isRecord(v)) return null;
  if (typeof v.id !== "string") return null;
  if (typeof v.name !== "string") return null;
  if (typeof v.required !== "boolean") return null;
  if (typeof v.note !== "string") return null;
  const availability = asAvailability(v.availability);
  if (availability === null) return null;
  const pollVotes = asPollVotesMap(v.pollVotes);
  if (pollVotes === null) return null;
  if (typeof v.updatedAt !== "string") return null;
  // Additive since phase 8: absent reads as 0/"" (a row stored before this phase), but a value of
  // the wrong type is a damaged shape, not a missing one.
  if (v.saveSeq !== undefined && typeof v.saveSeq !== "number") return null;
  if (v.origin !== undefined && typeof v.origin !== "string") return null;
  return {
    id: v.id,
    name: v.name,
    required: v.required,
    note: v.note,
    availability,
    pollVotes,
    updatedAt: v.updatedAt,
    saveSeq: typeof v.saveSeq === "number" ? v.saveSeq : 0,
    origin: typeof v.origin === "string" ? v.origin : "",
  };
}

function asPollOption(v: unknown): { id: string; label: string } | null {
  if (!isRecord(v)) return null;
  if (typeof v.id !== "string" || typeof v.label !== "string") return null;
  return { id: v.id, label: v.label };
}

function asPollDef(v: unknown): PollDef | null {
  if (!isRecord(v)) return null;
  if (typeof v.id !== "string" || typeof v.question !== "string") return null;
  if (!Array.isArray(v.options)) return null;
  const options: { id: string; label: string }[] = [];
  for (const o of v.options) {
    const opt = asPollOption(o);
    if (opt === null) return null;
    options.push(opt);
  }
  return { id: v.id, question: v.question, options };
}

function asLocked(v: unknown): Locked | null {
  if (!isRecord(v)) return null;
  if (typeof v.slotStart !== "number" || typeof v.durationMin !== "number") return null;
  if (!isRecord(v.pollWinners)) return null;
  const pollWinners: Record<string, string> = Object.create(null) as Record<string, string>;
  for (const [k, val] of Object.entries(v.pollWinners)) {
    if (typeof val !== "string") return null;
    pollWinners[k] = val;
  }
  if (typeof v.lockedAt !== "string") return null;
  const available = asStringArray(v.available);
  const missing = asStringArray(v.missing);
  if (available === null || missing === null) return null;
  return {
    slotStart: v.slotStart,
    durationMin: v.durationMin,
    pollWinners,
    lockedAt: v.lockedAt,
    available,
    missing,
  };
}

export function asEventState(v: unknown): EventState | null {
  if (!isRecord(v)) return null;
  if (typeof v.rev !== "number" || !Number.isInteger(v.rev)) return null;
  if (!isRecord(v.event)) return null;
  const ev = v.event;
  if (typeof ev.id !== "string" || typeof ev.title !== "string") return null;
  if (typeof ev.note !== "string" || typeof ev.timezone !== "string") return null;
  const dates = asStringArray(ev.dates);
  if (dates === null) return null;
  if (typeof ev.dayStartMin !== "number" || typeof ev.dayEndMin !== "number") return null;
  if (ev.slotMin !== 30 || typeof ev.durationMin !== "number") return null;
  if (!Array.isArray(ev.polls)) return null;
  const polls: PollDef[] = [];
  for (const p of ev.polls) {
    const pd = asPollDef(p);
    if (pd === null) return null;
    polls.push(pd);
  }
  if (ev.deadline !== null && typeof ev.deadline !== "string") return null;
  if (typeof ev.createdAt !== "string") return null;
  if (!Array.isArray(v.participants)) return null;
  const participants: Participant[] = [];
  for (const p of v.participants) {
    const parsed = asParticipant(p);
    if (parsed === null) return null;
    participants.push(parsed);
  }
  const expected = asStringArray(v.expected);
  if (expected === null) return null;
  let locked: Locked | null = null;
  if (v.locked !== null && v.locked !== undefined) {
    locked = asLocked(v.locked);
    if (locked === null) return null;
  }
  const event: EventDef = {
    id: ev.id,
    title: ev.title,
    note: ev.note,
    timezone: ev.timezone,
    dates,
    dayStartMin: ev.dayStartMin,
    dayEndMin: ev.dayEndMin,
    slotMin: 30,
    durationMin: ev.durationMin,
    polls,
    deadline: ev.deadline,
    createdAt: ev.createdAt,
  };
  return { rev: v.rev, event, participants, expected, locked };
}

export function asSaveResponse(v: unknown): SaveResponse | null {
  if (!isRecord(v)) return null;
  const state = asEventState(v.state);
  if (!state) return null;
  const participant = asParticipant(v.participant);
  if (!participant) return null;
  if (typeof v.joined !== "boolean" || typeof v.ignored !== "number") return null;
  if (v.stale !== undefined && typeof v.stale !== "boolean") return null;
  return {
    state,
    participant,
    joined: v.joined,
    ignored: v.ignored,
    stale: v.stale === true,
  };
}

export function asStateWrap(v: unknown): { state: EventState } | null {
  if (!isRecord(v)) return null;
  const state = asEventState(v.state);
  return state ? { state } : null;
}

function defChanged(a: EventDef, b: EventDef): boolean {
  return (
    a.timezone !== b.timezone ||
    a.dayStartMin !== b.dayStartMin ||
    a.dayEndMin !== b.dayEndMin ||
    a.slotMin !== b.slotMin ||
    a.dates.join(",") !== b.dates.join(",") ||
    JSON.stringify(a.polls) !== JSON.stringify(b.polls)
  );
}

function pruneDraft(draft: Draft, event: EventDef): { draft: Draft; prunedCells: number } {
  const valid = new Set(slotStarts(event).map(String));
  const availability: Record<string, Level> = {};
  let prunedCells = 0;
  for (const [k, v] of Object.entries(draft.availability)) {
    if (valid.has(k)) availability[k] = v;
    else prunedCells += 1;
  }
  const pollMap = new Map(event.polls.map((p) => [p.id, new Set(p.options.map((o) => o.id))]));
  const pollVotes = emptyVoteMap();
  for (const [pollId, votes] of Object.entries(draft.pollVotes)) {
    const opts = pollMap.get(pollId);
    if (!opts) continue;
    const kept = Object.create(null) as Record<string, PollVote>;
    for (const [oid, vote] of Object.entries(votes)) {
      if (opts.has(oid)) kept[oid] = vote;
    }
    pollVotes[pollId] = kept;
  }
  return { draft: { ...draft, availability, pollVotes }, prunedCells };
}
